import {
  Add as AddIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { Box, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { formatHistoryDate } from '../services/domainFactCheckHistoryService';
import { getJudgementColor, getJudgementLabel } from '../services/domainFactCheckService';
import { verdictSealFromLabel } from '../theme/designTokens';
import type { DomainFactCheckHistoryItem } from '../types';

interface Props {
  history: DomainFactCheckHistoryItem[];
  selectedId: string | null;
  onSelect: (item: DomainFactCheckHistoryItem) => void;
  onDelete: (id: string) => void;
  onShare: (item: DomainFactCheckHistoryItem) => void;
  onNewVerification: () => void;
  loading?: boolean;
}

const DomainFactCheckHistorySidebar: React.FC<Props> = ({
  history,
  selectedId,
  onSelect,
  onDelete,
  onShare,
  onNewVerification,
  loading = false,
}) => {
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();
  const dark = themeMode === 'dark';

  // 3点メニュー（共有・削除）の状態
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<DomainFactCheckHistoryItem | null>(null);

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>, item: DomainFactCheckHistoryItem) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuItem(item);
  };
  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
        minHeight: 0,
      }}
    >
      {/* ヘッダー（mock .hh） */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: '15px',
          pt: '14px',
          pb: '12px',
          flexShrink: 0,
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <HistoryIcon sx={{ fontSize: 15, color: tokens.teal }} />
        <Typography sx={{ fontSize: 12, fontWeight: 900, color: tokens.muted }}>
          検証履歴
        </Typography>
        <Box
          component="span"
          sx={{
            ml: 'auto',
            fontSize: 10.5,
            fontWeight: 900,
            color: tokens.muted,
            bgcolor: dark ? 'rgba(255,255,255,0.08)' : '#eef1f5',
            borderRadius: 999,
            px: 1,
            py: '1px',
          }}
        >
          {history.length}
        </Box>
      </Box>

      {/* 新規検証 */}
      <Box sx={{ px: '11px', pt: '11px', flexShrink: 0 }}>
        <Box
          component="button"
          type="button"
          onClick={onNewVerification}
          sx={{
            width: '100%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            fontFamily: 'inherit',
            fontSize: 12.5,
            fontWeight: 800,
            color: tokens.teal,
            bgcolor: dark ? 'rgba(45,212,191,0.12)' : '#eafaf6',
            border: `1.5px solid ${dark ? 'rgba(45,212,191,0.4)' : 'rgba(13,148,136,.4)'}`,
            borderRadius: '10px',
            py: 1,
            cursor: 'pointer',
            transition: '.12s',
            '&:hover': { filter: 'brightness(0.97)' },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          新規検証
        </Box>
      </Box>

      {/* 履歴リスト（mock .hlist） */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, p: '11px' }}>
        {loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12, color: tokens.faint }}>読み込み中...</Typography>
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12, color: tokens.faint }}>履歴がありません</Typography>
          </Box>
        ) : (
          history.map((item) => {
            const vcol = getJudgementColor(item.result.judgement);
            const label = getJudgementLabel(item.result.judgement);
            const on = item.id === selectedId;
            return (
              <Box
                key={item.id}
                onClick={() => onSelect(item)}
                sx={{
                  position: 'relative',
                  border: `1px solid ${on ? tokens.teal : tokens.line}`,
                  borderRadius: '12px',
                  p: '11px 30px 11px 15px',
                  mb: '9px',
                  cursor: 'pointer',
                  transition: '.14s',
                  background: on
                    ? dark
                      ? 'rgba(45,212,191,0.1)'
                      : '#f6fdfb'
                    : dark
                      ? 'rgba(255,255,255,0.03)'
                      : '#fff',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 8,
                    bottom: 8,
                    width: 4,
                    borderRadius: '0 3px 3px 0',
                    background: vcol,
                  },
                  '&:hover': {
                    borderColor: 'rgba(13,148,136,.45)',
                    boxShadow: '0 6px 16px rgba(30,30,70,.08)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {/* 3点メニュー（共有・削除） */}
                <IconButton
                  size="small"
                  onClick={(e) => handleOpenMenu(e, item)}
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 5,
                    p: '3px',
                    color: menuItem?.id === item.id ? tokens.ink : tokens.faint,
                    bgcolor:
                      menuItem?.id === item.id
                        ? dark
                          ? 'rgba(255,255,255,0.08)'
                          : '#eef1f5'
                        : 'transparent',
                    '&:hover': {
                      bgcolor: dark ? 'rgba(255,255,255,0.08)' : '#eef1f5',
                      color: tokens.ink,
                    },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </IconButton>

                {/* 主張（2行まで） */}
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.45,
                    color: tokens.ink,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.claim}
                </Typography>

                {/* 判定 + 日付（mock .hm） */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mt: 1.1 }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: 9.5,
                      fontWeight: 900,
                      px: 1,
                      py: '2px',
                      borderRadius: 999,
                      color: vcol,
                      bgcolor: `${vcol}1a`,
                    }}
                  >
                    <Box
                      component="img"
                      src={verdictSealFromLabel(label)}
                      alt=""
                      sx={{ width: 12, height: 12, objectFit: 'contain' }}
                    />
                    {label}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: tokens.faint,
                      ml: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatHistoryDate(item.created_at)}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* 共有・削除メニュー */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 172,
              borderRadius: '10px',
              border: `1px solid ${tokens.line}`,
              boxShadow: '0 12px 28px rgba(20,24,40,.18)',
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuItem) onShare(menuItem);
            handleCloseMenu();
          }}
          sx={{ fontSize: 12, fontWeight: 700, color: tokens.ink, py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 1, color: tokens.muted }}>
            <ShareIcon sx={{ fontSize: 15 }} />
          </ListItemIcon>
          共有
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuItem) onDelete(menuItem.id);
            handleCloseMenu();
          }}
          sx={{ fontSize: 12, fontWeight: 700, color: '#dc2626', py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 1, color: '#dc2626' }}>
            <DeleteIcon sx={{ fontSize: 15 }} />
          </ListItemIcon>
          削除
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DomainFactCheckHistorySidebar;
